import { useUser } from '@clerk/clerk-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Shield,
  ShieldAlert,
  Trash2,
  Upload,
  UserCheck,
  UserMinus,
  UserX,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { OrgMemberInfo } from '@/api/organizations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDepartments } from '@/features/departments/api/use-departments';
import {
  useDeleteEmployee,
  useEmployees,
  useEmployeeStats,
  useToggleEmployeeStatus,
} from '@/features/employees/api/use-employees';
import { useEmployeeAccess } from '@/features/employees/hooks/use-employee-access';
import { CONSENT_STATUS_LABELS, type EmployeeListItem } from '@/features/employees/types';
import { useOrgMembers } from '@/features/settings/subsidiaries/api/use-org-tree';
import { useRemoveMember } from '@/features/team/api/use-remove-member';
import { useUpdateMemberRole } from '@/features/team/api/use-update-member-role';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useRoles } from '@/hooks/use-roles';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/employees/')({
  component: EmployeeListPage,
});

function useOrgId() {
  return useOrganizationId();
}

function getInitials(name: string): string {
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
}

function EmployeeListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { canManageEmployees, isLoading: accessLoading } = useEmployeeAccess();

  if (accessLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canManageEmployees) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
          <h2 className="text-lg font-semibold">{t('accessDenied.title')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('accessDenied.employeeHubDenied')}
          </p>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/dashboard' })}
            className="mt-4"
          >
            {t('accessDenied.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('employees.pageTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('employees.pageDescription')}
        </p>
      </div>

      <StatsCards />

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">
            <Shield className="mr-2 h-4 w-4" />
            {t('employees.membersTab')}
          </TabsTrigger>
          <TabsTrigger value="employees">
            <Users className="mr-2 h-4 w-4" />
            {t('employees.employeeRecordsTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <MembersTab />
        </TabsContent>
        <TabsContent value="employees" className="mt-6">
          <EmployeesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─── Stats Cards ─── */

function StatsCards() {
  const orgId = useOrgId();
  const { t } = useTranslation('common');
  const { data: stats } = useEmployeeStats();
  const { data: membersData } = useOrgMembers(orgId);

  const memberCount = membersData?.totalCount ?? 0;
  const directMembers = membersData?.members.filter((m) => m.scope === 'direct').length ?? 0;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold">{memberCount}</p>
            <p className="text-xs text-muted-foreground">
              {t('employees.platformMembers')}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
            <KeyRound className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{directMembers}</p>
            <p className="text-xs text-muted-foreground">{t('employees.directMembers')}</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
            <Users className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats?.total ?? 0}</p>
            <p className="text-xs text-muted-foreground">{t('employees.employeeRecords')}</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
            <UserCheck className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats?.active ?? 0}</p>
            <p className="text-xs text-muted-foreground">{t('employees.activeEmployees')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Members Tab ─── */

function MembersTab() {
  const orgId = useOrgId();
  const { user } = useUser();
  const { t } = useTranslation('common');
  const { data, isLoading, error, refetch } = useOrgMembers(orgId);
  const { roles: apiRoles } = useRoles({ orgId });
  const removeMember = useRemoveMember({ orgId: orgId ?? '' });
  const updateRole = useUpdateMemberRole({ orgId: orgId ?? '' });
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [removeTarget, setRemoveTarget] = useState<OrgMemberInfo | null>(null);
  const [roleTarget, setRoleTarget] = useState<OrgMemberInfo | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const members = data?.members ?? [];

  // Derive available roles from the members list when the roles API returns empty
  // (e.g. due to missing org:roles:read permission)
  const roles = apiRoles.length > 0
    ? apiRoles
    : (() => {
        const seen = new Map<string, string>();
        for (const m of members) {
          if (m.roleId && m.role && !seen.has(m.roleId)) {
            seen.set(m.roleId, m.role);
          }
        }
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
      })();

  const currentUserIsSuperAdmin =
    (user?.publicMetadata as { role?: string } | undefined)?.role === 'super_admin';

  // Find current user's org role from the members list
  const currentUserMember = members.find((m) => m.userId === user?.id);
  const currentUserIsAdmin =
    currentUserIsSuperAdmin || currentUserMember?.role?.toLowerCase() === 'admin';

  const handleRemoveMember = async () => {
    if (!removeTarget) return;
    try {
      await removeMember.mutateAsync(removeTarget.id);
      toast.success(t('employees.memberRemoved', { name: removeTarget.userName ?? 'Member' }));
      setRemoveTarget(null);
    } catch {
      toast.error(t('employees.failedToRemoveMember'));
    }
  };

  const handleChangeRole = async () => {
    if (!roleTarget || !selectedRoleId) return;
    try {
      await updateRole.mutateAsync({ membershipId: roleTarget.id, roleId: selectedRoleId });
      const newRoleName = roles.find((r) => r.id === selectedRoleId)?.name ?? 'new role';
      toast.success(t('employees.roleChanged', { name: roleTarget.userName ?? 'Member', role: newRoleName }));
      setRoleTarget(null);
      setSelectedRoleId('');
    } catch {
      toast.error(t('employees.failedToChangeRole'));
    }
  };

  const filtered = members.filter((m) => {
    if (search) {
      const q = search.toLowerCase();
      const nameMatch = m.userName?.toLowerCase().includes(q);
      const emailMatch = m.userEmail?.toLowerCase().includes(q);
      if (!nameMatch && !emailMatch) return false;
    }
    if (scopeFilter && m.scope !== scopeFilter) return false;
    return true;
  });

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="mb-4 text-sm text-destructive">{t('employees.failedToLoadMembers')}</p>
        <Button variant="outline" onClick={() => void refetch()}>
          {t('employees.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('employees.membersDescription')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('employees.searchByNameOrEmail')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            className="pl-9"
            data-testid="member-search"
          />
        </div>
        <NativeSelect
          options={[
            { value: '', label: t('employees.allAccessTypes') },
            { value: 'direct', label: t('employees.direct') },
            { value: 'inherited', label: t('employees.inherited') },
          ]}
          value={scopeFilter}
          onChange={setScopeFilter}
          className="w-40"
          data-testid="member-scope-filter"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !filtered.length ? (
        <EmptyState
          icon={Shield}
          title={t('employees.noMembersFound')}
          description={search || scopeFilter ? t('employees.adjustFilters') : t('employees.noMembersInOrg')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('employees.userColumn')}</TableHead>
                  <TableHead>{t('employees.roleColumn')}</TableHead>
                  <TableHead>{t('employees.accessTypeColumn')}</TableHead>
                  <TableHead>{t('employees.organizationAccessColumn')}</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    currentUserId={user?.id}
                    isSuperAdmin={currentUserIsSuperAdmin}
                    isAdmin={currentUserIsAdmin}
                    onRemove={setRemoveTarget}
                    onChangeRole={(m) => {
                      setRoleTarget(m);
                      setSelectedRoleId(m.roleId ?? '');
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Remove Member Confirmation */}
      <AlertDialog open={Boolean(removeTarget)} onOpenChange={() => { setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employees.removeMember')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employees.removeMemberConfirm', { name: removeTarget?.userName ?? 'this member' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employees.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleRemoveMember()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('employees.removeMember')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      <AlertDialog open={Boolean(roleTarget)} onOpenChange={() => { setRoleTarget(null); setSelectedRoleId(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employees.changeRole')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employees.changeRoleDescription', { name: roleTarget?.userName ?? 'this member' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <NativeSelect
              options={roles
                .filter((r) => {
                  // Non-super admins cannot assign the Admin role
                  if (!currentUserIsSuperAdmin && r.name.toLowerCase() === 'admin') return false;
                  return true;
                })
                .map((r) => ({ value: r.id, label: r.name }))}
              value={selectedRoleId}
              onChange={setSelectedRoleId}
              className="w-full"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employees.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleChangeRole()}
              disabled={!selectedRoleId || selectedRoleId === roleTarget?.roleId}
            >
              {t('buttons.save')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface MemberRowProps {
  member: OrgMemberInfo;
  currentUserId: string | undefined;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  onRemove: (member: OrgMemberInfo) => void;
  onChangeRole: (member: OrgMemberInfo) => void;
}

function MemberRow({ member, currentUserId, isSuperAdmin, isAdmin, onRemove, onChangeRole }: MemberRowProps) {
  const { t } = useTranslation('common');
  const isCurrentUser = Boolean(currentUserId && member.userId === currentUserId);
  const [expanded, setExpanded] = useState(false);
  const orgAccessCount = member.organizationAccess.length;

  return (
    <>
      <TableRow data-testid={`member-row-${member.userId}`}>
        <TableCell>
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={member.userAvatarUrl ?? undefined} />
              <AvatarFallback className="text-xs">
                {member.userName ? getInitials(member.userName) : '?'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-medium">{member.userName ?? 'Unknown'}</p>
              <p className="truncate text-xs text-muted-foreground">{member.userEmail}</p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            {isSuperAdmin && member.userId === currentUserId && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                <ShieldAlert className="mr-1 h-3 w-3" />
                {t('employees.superAdmin')}
              </Badge>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant={
                      member.role === 'Admin'
                        ? 'default'
                        : member.role === 'Manager'
                          ? 'secondary'
                          : 'outline'
                    }
                  >
                    {member.role ?? 'Unknown'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {member.role === 'Admin'
                      ? t('employees.fullAccess')
                      : member.role === 'Manager'
                        ? t('employees.managerAccess')
                        : member.role === 'Member'
                          ? t('employees.memberAccess')
                          : t('employees.viewerAccess')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-1">
            <Badge variant={member.scope === 'direct' ? 'default' : 'outline'} className="w-fit">
              {member.scope === 'direct' ? t('employees.direct') : t('employees.inherited')}
            </Badge>
            {member.scope === 'inherited' && member.inheritedFromOrgName && (
              <span className="text-xs text-muted-foreground">
                via {member.inheritedFromOrgName}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          {orgAccessCount > 0 ? (
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => { setExpanded(!expanded); }}
            >
              <Building2 className="h-3.5 w-3.5" />
              <span>
                {orgAccessCount} org{orgAccessCount !== 1 ? 's' : ''}
              </span>
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell>
          {(() => {
            // No actions on yourself or inherited members
            if (member.userId === currentUserId || member.scope !== 'direct') return null;
            // Must be at least an admin to manage members
            if (!isAdmin) return null;

            const targetIsAdmin = member.role?.toLowerCase() === 'admin';
            // Admins (non-super) cannot manage other admins
            const canChangeRole = isSuperAdmin || (!targetIsAdmin);
            const canRemove = isSuperAdmin || (!targetIsAdmin);

            if (!canChangeRole && !canRemove) return null;

            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canChangeRole && (
                    <DropdownMenuItem onClick={() => { onChangeRole(member); }}>
                      <Shield className="mr-2 h-4 w-4" />
                      {t('employees.changeRole')}
                    </DropdownMenuItem>
                  )}
                  {canRemove && (
                    <>
                      {canChangeRole && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => { onRemove(member); }}
                      >
                        <UserMinus className="mr-2 h-4 w-4" />
                        {t('employees.removeMember')}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={5} className="py-2 pl-16">
            <div className="space-y-1">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t('employees.orgAccessFor', { name: member.userName })}
              </p>
              <div className="grid gap-1.5">
                {member.organizationAccess.map((access) => (
                  <div
                    key={access.organizationId}
                    className="flex items-center gap-3 text-sm"
                  >
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate">
                      {access.organizationName ?? access.organizationId}
                    </span>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {access.role ?? 'Unknown'}
                    </Badge>
                    <Badge
                      variant={access.scope === 'direct' ? 'default' : 'outline'}
                      className="shrink-0 text-xs"
                    >
                      {access.scope}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/* ─── Employees Tab ─── */

function EmployeesTab() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [consentFilter, setConsentFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [deleteTarget, setDeleteTarget] = useState<EmployeeListItem | null>(null);

  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (search) params.search = search;
  if (department) params.departmentId = department;
  if (statusFilter) params.is_active = statusFilter;
  if (consentFilter) params.consent_status = consentFilter;

  const { data, isLoading } = useEmployees(params);
  const { data: deptData } = useDepartments({ page_size: '200' });
  const deleteMutation = useDeleteEmployee();
  const toggleStatusMutation = useToggleEmployeeStatus();

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('employees.employeeDeleted', { name: deleteTarget.name }));
      setDeleteTarget(null);
    } catch {
      toast.error(i18n.t('settings:toast.failedToDeleteEmployee'));
    }
  };

  const handleToggleStatus = async (emp: EmployeeListItem) => {
    try {
      await toggleStatusMutation.mutateAsync({
        employeeId: emp.id,
        isActive: !emp.isActive,
      });
      toast.success(emp.isActive ? t('employees.employeeDeactivated', { name: emp.name }) : t('employees.employeeActivated', { name: emp.name }));
    } catch {
      toast.error(i18n.t('settings:toast.failedToUpdateStatus'));
    }
  };

  const departmentOptions = [
    { value: '', label: t('employees.allDepartments') },
    ...(deptData?.items.map((d) => ({ value: d.id, label: d.name })) ?? []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('employees.employeesDescription')}
        </p>
        <div className="flex items-center gap-2">
          <Link to="/employees/import" search={{ uploadId: undefined }}>
            <Button variant="outline" data-testid="import-employees-button">
              <Upload className="mr-2 h-4 w-4" />
              {t('employees.importCsv')}
            </Button>
          </Link>
          <Link to="/employees/new">
            <Button data-testid="add-employee-button">
              <Plus className="mr-2 h-4 w-4" />
              {t('employees.addEmployee')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('employees.searchByNameOrEmail')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9"
            data-testid="employee-search"
          />
        </div>
        <NativeSelect
          options={departmentOptions}
          value={department}
          onChange={(val) => {
            setDepartment(val);
            setPage(1);
          }}
          className="w-44"
        />
        <NativeSelect
          options={[
            { value: '', label: t('employees.allStatus') },
            { value: 'true', label: t('employees.active') },
            { value: 'false', label: t('employees.inactive') },
          ]}
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val);
            setPage(1);
          }}
          className="w-32"
        />
        <NativeSelect
          options={[
            { value: '', label: t('employees.allConsent') },
            { value: 'pending', label: t('employees.pending') },
            { value: 'granted', label: t('employees.granted') },
            { value: 'revoked', label: t('employees.revoked') },
          ]}
          value={consentFilter}
          onChange={(val) => {
            setConsentFilter(val);
            setPage(1);
          }}
          className="w-36"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={Users}
          title={t('employees.noEmployeesFound')}
          description={
            search || department || statusFilter || consentFilter
              ? t('employees.adjustFilters')
              : t('employees.noEmployeesHint')
          }
          {...(!search && !department && !statusFilter && !consentFilter
            ? {
                action: {
                  label: t('employees.addEmployee'),
                  onClick: () => void navigate({ to: '/employees/new' }),
                },
              }
            : {})}
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('employees.nameColumn')}</TableHead>
                    <TableHead>{t('employees.emailColumn')}</TableHead>
                    <TableHead>{t('employees.departmentColumn')}</TableHead>
                    <TableHead>{t('employees.facilityColumn')}</TableHead>
                    <TableHead>{t('employees.orgAccessColumn')}</TableHead>
                    <TableHead>{t('employees.statusColumn')}</TableHead>
                    <TableHead>{t('employees.consentColumn')}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((emp: EmployeeListItem) => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell>
                        <Link
                          to="/employees/$employeeId"
                          params={{ employeeId: emp.id }}
                          className="font-medium hover:underline"
                        >
                          {emp.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{emp.email}</TableCell>
                      <TableCell>{emp.departmentName ?? '-'}</TableCell>
                      <TableCell>{emp.facilityName ?? '-'}</TableCell>
                      <TableCell>
                        {emp.organizationAccess.length > 0 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Building2 className="h-3.5 w-3.5" />
                                  {emp.organizationAccess.length}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <div className="space-y-1">
                                  {emp.organizationAccess.map((oa) => (
                                    <p key={oa.id} className="text-xs">
                                      {oa.name}
                                    </p>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={emp.isActive ? 'default' : 'secondary'}>
                          {emp.isActive ? t('employees.active') : t('employees.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            emp.consentStatus === 'granted'
                              ? 'default'
                              : emp.consentStatus === 'revoked'
                                ? 'destructive'
                                : 'outline'
                          }
                        >
                          {CONSENT_STATUS_LABELS[emp.consentStatus]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              data-testid={`employee-actions-${emp.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                void navigate({
                                  to: '/employees/$employeeId',
                                  params: { employeeId: emp.id },
                                })
                              }
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('employees.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => void handleToggleStatus(emp)}
                            >
                              {emp.isActive ? (
                                <>
                                  <UserX className="mr-2 h-4 w-4" />
                                  {t('employees.deactivate')}
                                </>
                              ) : (
                                <>
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  {t('employees.activate')}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => { setDeleteTarget(emp); }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('employees.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('employees.showingPagination', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, data.total), total: data.total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => { setPage((p) => p - 1); }}
                >
                  {t('employees.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => { setPage((p) => p + 1); }}
                >
                  {t('employees.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => { setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employees.deleteEmployee')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employees.deleteEmployeeConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employees.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('employees.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
